import { useQuery } from '@tanstack/react-query';
import { Gauge } from 'lucide-react';
import type { DatadogCoverageReport, PassportVerification } from '@siliconx/shared';
import { VerificationOutcome } from '@siliconx/shared/ui/VerificationOutcome';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { Card, CardLabel } from '../asset-v2/shared';

/**
 * SIL-585 (Wave 4/UI, spec §9) — the evidence-sources panel: the org's
 * Datadog config three-state (`scopedEccCoverage.state`, derived from
 * `datadog_integration.resolved_scoped_families`) AND this asset's latest
 * Active Verification state, shown side by side.
 *
 * Deliberately TWO independent queries, never merged: "keep the two
 * independent in the data layer (never merge with resolved_scoped_families)"
 * (§9). The Datadog side is org-scoped (one integration per org); the
 * verification side is per-asset — that asymmetry is real, not an oversight,
 * and is exactly why they can never be one derived value.
 */

interface DatadogStatus {
  connected: boolean;
  scopedEccCoverage?: { state: 'not_configured' | 'partial' | 'full' };
  coverage?: DatadogCoverageReport;
}

const SCOPED_ECC_LABEL: Record<'not_configured' | 'partial' | 'full', { label: string; className: string }> = {
  not_configured: { label: 'Not configured', className: 'bg-muted text-muted-foreground' },
  partial: { label: 'Partially configured', className: 'bg-amber-100 text-amber-800' },
  full: { label: 'Fully configured', className: 'bg-emerald-100 text-emerald-700' },
};

function DatadogConfigState() {
  const { data, isLoading } = useQuery<DatadogStatus>({
    queryKey: ['client-integrations-datadog-status'],
    queryFn: async () => {
      const res = await api.api.client.integrations.datadog.status.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as DatadogStatus;
    },
  });

  if (isLoading) return <p className="text-xs text-muted-foreground">Loading…</p>;

  if (!data?.connected) {
    return (
      <div className="space-y-1">
        <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
          Not connected
        </span>
        <p className="text-xs text-muted-foreground">
          Connect a telemetry provider under Integrations to enable telemetry-based scoring for this fleet.
        </p>
      </div>
    );
  }

  const state = data.scopedEccCoverage?.state ?? 'not_configured';
  const badge = SCOPED_ECC_LABEL[state];

  return (
    <div className="space-y-1">
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.className}`}>
        {badge.label}
      </span>
      <p className="text-xs text-muted-foreground">
        Org-level telemetry configuration — governs the passive scoring signal this
        asset's health score draws on.
      </p>
    </div>
  );
}

function VerificationState({ assetId }: { assetId: string }) {
  const { data, isLoading } = useQuery<PassportVerification>({
    queryKey: qk.assets.verificationState(assetId),
    queryFn: async () => {
      const res = await api.api.client.verification.assets[':assetId'].state.$get({
        param: { assetId },
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as PassportVerification;
    },
  });

  if (isLoading) return <p className="text-xs text-muted-foreground">Loading…</p>;

  return <VerificationOutcome verification={data ?? { kind: 'none' }} compact />;
}

export function EvidenceSourcesPanel({ assetId }: { assetId: string }) {
  return (
    <Card className="space-y-3">
      <div className="flex items-center gap-2">
        <Gauge className="size-4 text-muted-foreground" />
        <CardLabel>Evidence sources</CardLabel>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Telemetry configuration
          </div>
          <div className="mt-2">
            <DatadogConfigState />
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Active verification
          </div>
          <div className="mt-2">
            <VerificationState assetId={assetId} />
          </div>
        </div>
      </div>
    </Card>
  );
}
